import { ContactFormFields } from '@transcend-io/contact-form-schema';
import { cleanEmail } from '@transcend-io/segment/core';

type ChiliPiperCallback = (data?: unknown) => void;

type TriggerSchedulingModalProps = {
  fields: ContactFormFields;
  routerId: string;
  onClose?: ChiliPiperCallback;
  onRouted?: ChiliPiperCallback;
  onDisqualified?: ChiliPiperCallback;
  onError?: ChiliPiperCallback;
  onSuccess?: ChiliPiperCallback;
};

function logChiliPiper(event: string, data?: unknown) {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  if (data === undefined) {
    console.info(`[ChiliPiper] ${event}`);
    return;
  }
  console.info(`[ChiliPiper] ${event}`, data);
}

/**
 * Submission handler for ChiliPiper scheduling modal.
 * Lets Concierge mount its own popup on the page (no custom DOM target).
 *
 * @see https://help.chilipiper.com/hc/en-us/articles/360053800373-About-Concierge-Snippet-and-JS-API
 */
export function triggerSchedulingModal({
  fields,
  routerId,
  onClose,
  onRouted,
  onDisqualified,
  onError,
  onSuccess,
}: TriggerSchedulingModalProps) {
  try {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      if (!window.ChiliPiper) {
        onClose?.();
        throw new Error('ChiliPiper was not loaded!');
      }

      const cleanedEmail = cleanEmail(fields.email);

      if (cleanedEmail === 'force-error@transcend.io') {
        throw new Error('Force error');
      }

      window.ChiliPiper.submit('transcend', routerId, {
        trigger: 'ThirdPartyForm',
        debug: process.env.NODE_ENV === 'development',
        lead: {
          Email: cleanedEmail,
          email: cleanedEmail,
          'First Name': fields.firstName,
          firstName: fields.firstName,
          'Last Name': fields.lastName,
          lastName: fields.lastName,
          Title: fields.title,
          Company: fields.company,
          company: fields.company,
          State: fields.state,
          state: fields.state,
          Country: fields.country,
          country: fields.country,
          'Company Size': fields.companySize,
          companySize: fields.companySize,
        },
        onRouted: (data) => {
          logChiliPiper(
            'onRouted — routing finished (calendar should stay open)',
            data,
          );
          onRouted?.(data);
        },
        onSuccess: (data) => {
          logChiliPiper('onSuccess — meeting booked', data);
          onSuccess?.(data);
        },
        onError: (data) => {
          logChiliPiper(
            'onError — no queue match / cannot route (calendar will not stay open)',
            data,
          );
          onError?.(data);
        },
        onDisqualified: (data) => {
          logChiliPiper('onDisqualified — router disqualified this lead', data);
          onDisqualified?.(data);
        },
        onClose: (data) => {
          logChiliPiper('onClose — calendar closed without booking', data);
          onClose?.(data);
        },
      });
    }
  } catch (err) {
    onError?.();
    console.error(err);
  }
}
